import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Copy, Users, Crown, Check, MessageCircle, Linkedin, Twitter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/utils/constants";

export const ReferralsView = () => {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/referrals/me`).then((r) => setData(r.data)).catch(() => toast.error("Failed to load referral info"));
  }, []);

  if (!data) return <div className="p-6 text-sm text-muted-foreground">Loading...</div>;

  const link = `${window.location.origin}/?ref=${data.code}`;
  const shareText = "I manage my projects with Opero Smart — Kanban boards, AI task creation and team chat in one app. Join with my link and try it free:";
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); } catch { const el = document.createElement("textarea"); el.value = link; document.body.appendChild(el); el.select(); document.execCommand("copy"); el.remove(); }
    setCopied(true); toast.success("Referral link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto" data-testid="referrals-view">
      <div className="rounded-2xl border bg-gradient-to-br from-primary/10 via-purple-500/5 to-transparent p-6 sm:p-8 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center"><Gift className="h-5 w-5 text-primary" /></div>
          <div>
            <h2 className="font-heading text-xl font-bold">Refer a friend, get 1 month Pro free</h2>
            <p className="text-sm text-muted-foreground">Every friend who signs up with your link earns you 30 days of Pro — stackable, no limits.</p>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <Input readOnly value={link} className="font-mono text-xs sm:text-sm" data-testid="referral-link-input" onFocus={(e) => e.target.select()} />
          <Button onClick={copy} data-testid="referral-copy-btn">{copied ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}{copied ? "Copied" : "Copy"}</Button>
        </div>
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <span className="text-xs text-muted-foreground mr-1">Share via:</span>
          <Button asChild size="sm" variant="outline" className="h-8 text-xs" data-testid="referral-share-whatsapp">
            <a href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + link)}`} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="h-3.5 w-3.5 mr-1.5 text-emerald-500" />WhatsApp
            </a>
          </Button>
          <Button asChild size="sm" variant="outline" className="h-8 text-xs" data-testid="referral-share-linkedin">
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`} target="_blank" rel="noopener noreferrer">
              <Linkedin className="h-3.5 w-3.5 mr-1.5 text-sky-600" />LinkedIn
            </a>
          </Button>
          <Button asChild size="sm" variant="outline" className="h-8 text-xs" data-testid="referral-share-x">
            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(link)}`} target="_blank" rel="noopener noreferrer">
              <Twitter className="h-3.5 w-3.5 mr-1.5" />X
            </a>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-card border rounded-xl p-5" data-testid="referral-stat-count">
          <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1"><Users className="h-3.5 w-3.5" />Friends referred</div>
          <div className="text-2xl font-bold">{data.referred_count}</div>
        </div>
        <div className="bg-card border rounded-xl p-5" data-testid="referral-stat-months">
          <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1"><Gift className="h-3.5 w-3.5" />Free months earned</div>
          <div className="text-2xl font-bold">{data.months_earned}</div>
        </div>
        <div className="bg-card border rounded-xl p-5" data-testid="referral-stat-until">
          <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1"><Crown className="h-3.5 w-3.5 text-amber-500" />Free Pro until</div>
          <div className="text-2xl font-bold">{data.pro_until ? new Date(data.pro_until).toLocaleDateString() : "—"}</div>
        </div>
      </div>

      <div className="mt-6 bg-muted/40 rounded-xl p-5 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <p>1. Share your link with teammates, friends, or on social media.</p>
        <p>2. They sign up for a free Opero Smart account using your link.</p>
        <p>3. You instantly get 30 days of Pro credited to your account. Rewards stack — 12 referrals = a full year free.</p>
      </div>
    </div>
  );
};
